import classNames from 'classnames';
import Flex from 'components/common/Flex';
import PropTypes from 'prop-types';
import React from 'react';
import { Spinner } from 'react-bootstrap';

/**
 * A subtle, unbranded loading indicator (Bootstrap border spinner).
 *
 * Replaces the previous branded clover spinner everywhere a general-purpose
 * loader is needed. The `grow` prop (kept for backward compatibility with the
 * former FontAwesome-based spinner) is mapped to a capped rem size so existing
 * call sites keep a sensible, subtle footprint. A `role="status"` with
 * visually-hidden text announces the loading state to assistive technology.
 *
 * @param {Object} props - The component props
 * @param {string} [props.grow='3'] - Legacy size hint; mapped to a rem size
 * @param {string} [props.color='primary'] - Bootstrap variant for the spinner
 * @param {string} [props.className] - Extra classes for the spinner
 * @returns {JSX.Element} The rendered spinner
 */
const LoadingSpinner = ({ grow = '3', color = 'primary', className }) => {
  const parsed = Number(grow);
  const sizeRem = Math.min(
    3,
    Math.max(1, (Number.isNaN(parsed) ? 3 : parsed) / 24 + 1)
  );
  return (
    <Flex justifyContent='around' alignItems='center' role='status'>
      <Spinner
        animation='border'
        variant={color}
        className={classNames(className)}
        style={{ width: `${sizeRem}rem`, height: `${sizeRem}rem` }}
      />
      <span className='visually-hidden'>Loading…</span>
    </Flex>
  );
};

LoadingSpinner.propTypes = {
  grow: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string
};

export default LoadingSpinner;
